import { useState } from 'react';
import Button from '../../components/ui/Button';
import { MdMailOutline } from 'react-icons/md';
import { HiArrowRight } from 'react-icons/hi';
import { useTheme } from '../../contexts/ThemeContext';
import { validateEmail } from '../../utils/validations';
import { apiCall } from '../../api/fetchClient';
import { SEND_OTP_URL } from '../../api/url';
import { strings } from '../../strings/strings';

const LoginPage = ({ onLoginSuccess }) => {
  const { isDark }                = useTheme();
  const [email, setEmail]         = useState('');
  const [errors, setErrors]       = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (errors.email) setErrors({ ...errors, email: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = validateEmail(email);
    if (!result.isValid) { setErrors({ email: result.message }); return; }

    setIsLoading(true);
    try {
      const response = await apiCall(SEND_OTP_URL, 'POST', { email });
      if (!!response?.success) onLoginSuccess(email);
    } catch (error) {
      setErrors({ email: error.message || strings.ERROR_SEND_OTP });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="animate-slideUp w-full">
      <form onSubmit={handleSubmit} noValidate className="space-y-6">
        <div>
          <label
            htmlFor="email"
            className={`block text-sm font-semibold mb-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
          >
            {strings.EMAIL_ADDRESS_LABEL} <span className="text-red-500">*</span>
          </label>
          <div className="relative group">
            <div className={`absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors duration-200 ${
              errors.email ? 'text-red-500' : 'text-slate-400 group-focus-within:text-primary'
            }`}>
              <MdMailOutline className="w-5 h-5" />
            </div>
            <input
              id="email"
              type="email"
              value={email}
              onChange={handleEmailChange}
              placeholder={strings.EMAIL_INPUT_PLACEHOLDER}
              required
              autoComplete="email"
              className={`w-full pl-12 pr-4 py-3.5 rounded-xl text-sm font-medium outline-none border transition-all duration-300 ${
                isDark
                  ? 'bg-slate-900/50 text-white placeholder-slate-500 border-slate-700 focus:bg-slate-900 focus:border-primary focus:ring-4 focus:ring-primary/20'
                  : 'bg-slate-50/50 text-slate-900 placeholder-slate-400 border-slate-200 focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/20 hover:bg-slate-50'
              } ${
                errors.email
                  ? '!border-red-500 !focus:ring-red-500/20'
                  : ''
              }`}
            />
          </div>
          {errors.email && (
            <p className="text-xs mt-2 text-red-500 font-medium animate-fadeIn">
              {errors.email}
            </p>
          )}
        </div>

        <Button
          type="submit"
          variant="primary"
          fullWidth
          size="lg"
          loading={isLoading}
          disabled={isLoading}
          className={`group relative overflow-hidden rounded-xl font-bold tracking-wide transition-all duration-300 shadow-lg py-3.5 ${
            isDark 
              ? 'bg-primary hover:bg-primary-dark text-slate-900 shadow-primary/20' 
              : 'bg-primary hover:bg-primary-dark text-white shadow-primary/30'
          }`}
        >
          <span className="relative z-10 flex items-center justify-center gap-2">
            {isLoading ? strings.BUTTON_LOADING : strings.BUTTON_CONTINUE_EMAIL}
            {!isLoading && (
              <HiArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
            )}
          </span>
        </Button>

        <div className="relative mt-8">
          <div className="absolute inset-0 flex items-center">
            <div className={`w-full border-t ${isDark ? 'border-slate-800' : 'border-slate-200'}`}></div>
          </div>
          <div className="relative flex justify-center text-xs font-semibold">
            <span className={`px-4 rounded-full ${isDark ? 'bg-slate-800 text-slate-400 border border-slate-800' : 'bg-white text-slate-400 border border-slate-200'}`}>
              {strings.SECURE_LOGIN_TEXT}
            </span>
          </div>
        </div>
      </form>
    </div>
  );
};

export default LoginPage;
